import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { IonTabs, mdTransitionAnimation, ViewDidEnter, ViewDidLeave, ViewWillEnter, ViewWillLeave } from '@ionic/angular';

import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { AppEventsService } from '../../core/services/app-events.service';
import { CartService } from '../../core/services/cart.service';
import { DeepLinkService } from '../../core/services/deep-link.service';
import { NavigationService } from '../../core/services/navigation.service';
import { OrdersService } from '../../core/services/orders.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';

const BACKGROUND_WIDTH_IN_PX = 44;

export const TAB_BAR_HEIGHT_IN_PX = 50.5; // 50px height + 0.5px border top

declare type LifecycleEventName = 'ionViewWillEnter' | 'ionViewDidEnter' | 'ionViewWillLeave' | 'ionViewDidLeave';

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.page.html',
  styleUrls: ['./tabs.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TabsPage implements OnInit, ViewWillEnter, ViewDidEnter, ViewWillLeave, ViewDidLeave, OnDestroy {
  @ViewChild(IonTabs)
  public tabs: IonTabs;

  public cartItemsCount$: Observable<number>;
  public activeOrdersCount$: Observable<number>;

  public selectedTabBackgroundSize = BACKGROUND_WIDTH_IN_PX;
  public selectedTabBackgroundOffset: number;

  private lastActiveTab?: HTMLElement;

  private tabWidth: number;
  private initialTabOffset: number;
  private shouldPropagateLifeCycleEvents = false;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private platformService: PlatformService,
    private settingsService: SettingsService,
    private cartService: CartService,
    private navigationService: NavigationService,
    private ordersService: OrdersService,
    private appEventsService: AppEventsService,
    private deepLinkService: DeepLinkService,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.tabs?.outlet?.activatedView?.element),
        filter((activeViewElement) => !!activeViewElement),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((activeViewelement) => {
        this.lastActiveTab = activeViewelement;
      });
  }

  ngOnInit() {
    // Tabs have a max width of 240px
    this.tabWidth = this.platformService.isCordova ? Math.min(240, this.platformService.width() / 4) : 0;
    this.initialTabOffset = this.platformService.isCordova ? (this.platformService.width() - 4 * this.tabWidth) / 2 : 0;

    this.cartItemsCount$ = this.cartService.cart$.pipe(
      map(() => this.cartService.getItemsCount()),
      distinctUntilChanged(),
    );

    this.activeOrdersCount$ = this.ordersService.activeOrders$.pipe(
      map((details) => details.count),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    );
  }

  // We need to manually propagate the events to the tabs
  // https://bilbayt.atlassian.net/browse/CA-2044
  // https://github.com/ionic-team/ionic-framework/issues/16834#issuecomment-631676434

  ionViewWillEnter() {
    this.propagateToActiveTab('ionViewWillEnter');
  }

  ionViewDidEnter() {
    this.propagateToActiveTab('ionViewDidEnter');
    this.appEventsService.dispatch('EnteredToHomePage');

    // We should propagate the lifecylcle events only AFTER the
    // home page has been inititalized otherwise the events may
    // be called twice!
    // https://bilbayt.atlassian.net/browse/CA-2066
    if (!this.shouldPropagateLifeCycleEvents) {
      this.shouldPropagateLifeCycleEvents = true;
    }
  }

  ionViewWillLeave() {
    this.propagateToActiveTab('ionViewWillLeave');
  }

  ionViewDidLeave() {
    this.propagateToActiveTab('ionViewDidLeave');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public handleHomePageTabSelected(tabsRef: IonTabs, event: MouseEvent): void {
    const urlWithoutParams = this.navigationService.getUrlWithoutParams();

    if (urlWithoutParams.includes('/vendors/home')) {
      event.stopImmediatePropagation();
      return;
    }

    if (tabsRef.outlet.canGoBack() || urlWithoutParams.includes('/vendors/search')) {
      event.stopImmediatePropagation();
      void this.navigationService.navigateBackTo('/vendors/home', mdTransitionAnimation);
      return;
    }
  }

  public tabWillChange({ tab }: { tab: string }): void {
    const directionOffset = this.settingsService.getLanguageDirection() === 'ltr' ? 1 : -1;
    const backgroundOffset = this.initialTabOffset + (this.tabWidth - BACKGROUND_WIDTH_IN_PX) / 2;

    switch (tab) {
      case 'items':
      case 'vendors':
        this.selectedTabBackgroundOffset = directionOffset * backgroundOffset;
        break;
      case 'contact':
        this.selectedTabBackgroundOffset = directionOffset * (2 * this.tabWidth + backgroundOffset);
        break;
      case 'account':
        this.selectedTabBackgroundOffset = directionOffset * (3 * this.tabWidth + backgroundOffset);
        break;
    }
  }

  public onOpenCart(): void {
    this.deepLinkService.openCartPage();
  }

  private propagateToActiveTab(eventName: LifecycleEventName) {
    if (!this.shouldPropagateLifeCycleEvents || !this.lastActiveTab) {
      return;
    }

    this.lastActiveTab.dispatchEvent(new CustomEvent(eventName));
  }
}
